import { IonCol, IonGrid, IonRow } from '@ionic/react';
import equal from 'deep-equal';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { BooleanParam, createEnumParam, StringParam, useQueryParams, withDefault } from 'use-query-params';

import EmployerSelect from '@components/fields/EmployerSelect';
import ExpertSelect from '@components/fields/ExpertSelect';
import TimePeriodField, { TimePeriodEnum } from '@components/fields/TimePeriodField';
import styles from '@components/forms/AdminJourneyFilters.module.css';
import Form from '@components/forms/Form';
import useMyAdmin from '@hooks/useMyAdmin';
import useMyExpert from '@hooks/useMyExpert';
import { ExpertGlobalRoleEnum } from '@typing/Generated';
import { onChangeWithPaid } from '@utils/formUtils';

type FormData = {
  employerId?: string | null;
  endDate?: string | null;
  expertId?: string | null;
  selfPaid?: boolean | null;
  startDate?: string | null;
  timePeriod?: TimePeriodEnum | null;
};

export type Filters = FormData;

type Props = {
  filters: Filters;
  onChange: (filterValues: FormData) => void;
  showEmployerFilter?: boolean;
};

const ExpertKPIsFilters = ({ filters, onChange, showEmployerFilter = false }: Props) => {
  const intl = useIntl();
  const myExpert = useMyExpert();
  const myAdmin = useMyAdmin();

  const timePeriodParam = createEnumParam<TimePeriodEnum>(Object.values(TimePeriodEnum));

  const [query, setQuery] = useQueryParams({
    employerId: withDefault({ ...StringParam, urlName: 'expertKPIsEmployerId' }, filters.employerId),
    employerName: { ...StringParam, urlName: 'expertKPIsEmployerName' },
    endDate: withDefault({ ...StringParam, urlName: 'expertKPIsEndDate' }, filters.endDate),
    expertId: withDefault({ ...StringParam, urlName: 'expertKPIsExpertId' }, filters.expertId),
    expertName: { ...StringParam, urlName: 'expertKPIsExpertName' },
    selfPaid: withDefault({ ...BooleanParam, urlName: 'packageGrantSelfPaid' }, filters.selfPaid),
    startDate: withDefault({ ...StringParam, urlName: 'expertKPIsStartDate' }, filters.startDate),
    timePeriod: withDefault({ ...timePeriodParam, urlName: 'expertKPIsTimePeriod' }, filters.timePeriod)
  });

  const initialValues: FormData = query;

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange'
  });

  const employerId = form.watch('employerId');
  const expertId = form.watch('expertId');
  const timePeriod = form.watch('timePeriod');
  const selfPaid = form.watch('selfPaid');
  const startDate = form.watch('startDate');
  const endDate = form.watch('endDate');

  useEffect(() => {
    if (myExpert || myAdmin) {
      const newExpertId = expertId ? expertId : null;
      const values = { employerId, endDate, expertId: newExpertId, selfPaid, startDate, timePeriod };
      if (!equal(values, filters)) {
        setQuery(values);
        onChangeWithPaid(onChange, values);
      }
    }
  }, [employerId, endDate, expertId, filters, myAdmin, myExpert, onChange, selfPaid, setQuery, startDate, timePeriod]);

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Form className={styles.form + ' ' + styles.noMarginTop} hookForm={form} onSubmit={() => {}}>
      <IonGrid>
        <IonRow className="ion-align-items-center">
          <TimePeriodField form={form} />
          {myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager && (
            <IonCol>
              <ExpertSelect<FormData>
                allowAny
                allowMe
                control={form.control}
                error={form.formState.errors.expertId}
                initialOption={{ label: query.expertName ?? '', value: query.expertId ?? '' }}
                label={intl.formatMessage({ id: 'forms.expertConsultsFilters.expertLabel' })}
                name="expertId"
                onSelection={(option: { label: string }) => {
                  setQuery({ ...query, ...{ expertName: option.label } });
                }}
              />
            </IonCol>
          )}
          <IonCol className={showEmployerFilter ? '' : 'ion-hide'}>
            <EmployerSelect<FormData>
              allowAny
              control={form.control}
              error={form.formState.errors.employerId}
              initialOption={{ label: query.employerName ?? '', value: query.employerId ?? '' }}
              label={intl.formatMessage({ id: 'forms.expertJourneysFilters.employerLabel' })}
              name="employerId"
              onSelection={(option: { label: string }) => {
                setQuery({ ...query, ...{ employerName: option.label } });
              }}
            />
          </IonCol>
        </IonRow>
      </IonGrid>
    </Form>
  );
};

export default ExpertKPIsFilters;
